import { Button, Form, message, Input, Col, Row, Modal, Select, Switch } from 'antd';
import AppHeader from 'components/AppHeader';
import React, { useState, useEffect } from 'react';
import accountApis from 'api/accountApis';
import orderPaymentApis from 'api/orderPaymentApis';
import { ContentRow } from 'components/ThemeComponents';
import { StyledTable, OFFSET_HEADER } from 'components/ThemeComponents';
import { fullDateTimeFormat } from 'helpers/dateTimeHelper';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'configs/AppRoutes';
import { current_account } from 'helpers/authorizeHelper';

export default function ListAiHubAccountsPage() {
  const navigate = useNavigate();
  const [passwordForm] = Form.useForm();
  const [orderPaymentForm] = Form.useForm();
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [isOrderPaymentModalVisible, setIsOrderPaymentModalVisible] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [paginate, setPaginate] = useState({});
  const [searchForm] = Form.useForm();
  const [exportLoading, setExportLoading] = useState({});

  const showPasswordModal = (id) => {
    setSelectedAccountId(id);
    setIsPasswordModalVisible(true);
  };

  const handleUpdatePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      const res = await accountApis.updatePassword(selectedAccountId, values);
      if (res.code === 200) {
        message.success('Password updated successfully');
        setIsPasswordModalVisible(false);
        passwordForm.resetFields();
      }
    } catch (error) {
      message.error(error.message || 'Failed to update password');
    }
  };

  async function fetchAccounts(params={}) {
    setIsFetching(true);
    params['from_source'] = 'ai_hub'
    try {
      const res = await accountApis.listing(params);
      if (res.code === 200) {
        setAccounts(res.data ?? []);
        setPaginate({page: res.page, limit: res.limit, total: res.total})
      }
    } catch (error) {
      message.info(error.message);
    }
    setIsFetching(false);
  }

  useEffect(() => {
    fetchAccounts()
  }, []);

  const onChangeTable = async(pagination, filters, sorter) => {
    console.log(sorter)
    var params = {
      page: pagination.current,
      limit: pagination.pageSize,
      data_sort: sorter.columnKey,
      order_sort: sorter.order
    }
    const args = Object.assign({}, params, searchForm.getFieldsValue())
    await fetchAccounts(args)
  }

  const onFinishSearchForm = async (values) => {
    const params = values || {}
    console.log(params)
    await fetchAccounts(params)
  };

  const handleAccessAccount = async (id) => {
    const res = await accountApis.getLinkAccessAccount(id);
    try {
      const res = await accountApis.getLinkAccessAccount(id);
      if (res.code === 200) {
        window.open(res.access_link,'_blank');
      }
    } catch (error) {
      message.info(error.message);
    }
  };

  const showOrderPaymentModal = (id) => {
    setSelectedAccountId(id);
    setIsOrderPaymentModalVisible(true);
  };

  const handleCreateOrderPayment = async () => {
    try {
      const values = await orderPaymentForm.validateFields();
      Modal.confirm({
        title: 'Confirm Order Payment Creation',
        content: 'Are you sure you want to create this order payment?',
        onOk: async () => {
          const params = {
            account_id: selectedAccountId,
            noted_by_id: current_account.id,
            ...values
          };
          const res = await orderPaymentApis.CreateAndCompletedOrderPayment(params);
          if (res.code === 200) {
            message.success('Order payment created successfully');
            setIsOrderPaymentModalVisible(false);
            orderPaymentForm.resetFields();
            
            // Get current pagination and filter values
            const currentPagination = {
              page: paginate.page,
              limit: paginate.limit
            };
            const currentFilters = searchForm.getFieldsValue();
            
            // Fetch accounts with current pagination and filters
            await fetchAccounts({
              ...currentPagination,
              ...currentFilters
            });
          }
        },
        onCancel() {}
      });
    } catch (error) {
      message.error(error.message || 'Failed to create order payment');
    }
  };

  const handleExport = async (type) => {
    setExportLoading(prev => ({ ...prev, [type]: true }));
    try {
      const res = await accountApis.exportAccounts(type);
      if (res.code === 200 && res.data && res.data.length > 0) {
        const headers = Object.keys(res.data[0]);
        const csvString = convertToCSV(res.data, headers);
        // Use a more specific filename based on the type
        let filenamePrefix = 'export';
        if (type === 'get_trial_accounts') filenamePrefix = 'trial_accounts';
        else if (type === 'get_expiring_accounts') filenamePrefix = 'expiring_accounts';
        else if (type === 'get_pending_payment_accounts') filenamePrefix = 'pending_payment_accounts';

        const filename = `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.csv`;
        downloadCSV(csvString, filename);
        message.success(`Exported ${type.replace(/_/g, ' ')} successfully`);
      } else if (res.code === 200 && (!res.data || res.data.length === 0)) {
         message.info(`No data found for ${type.replace(/_/g, ' ')} export.`);
      } else {
        message.error(res.message || `Failed to export ${type.replace(/_/g, ' ')}`);
      }
    } catch (error) {
      message.error(error.message || `Failed to export ${type.replace(/_/g, ' ')}`);
    } finally {
      setExportLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleFeatureToggle = async (feature, checked, record) => {
    try {
      const params = {};
      params.feature = feature
      params.checked = checked
      const res = await accountApis.updateFeatures(record.id, params);
      if (res.code === 200) {
        message.success(`${feature} feature ${checked ? 'enabled' : 'disabled'} successfully`);
        fetchAccounts({
          page: paginate.page,
          limit: paginate.limit,
          ...searchForm.getFieldsValue()
        });
      }
    } catch (error) {
      message.error(error.message || `Failed to update ${feature} feature`);
    }
  };
  

  const bottom = 'bottomRight';

  const columns = [
    {
      title: 'Basic Information',
      width: '30rem',
      render: (record) => (
        <div style={{ whiteSpace: 'pre-line' }}>
          <p><strong>ID:</strong> {record.id}</p>
          <p><strong>Name:</strong> {record.name}</p>
          <p><strong>Email:</strong> {record.email}</p>
          <p><strong>Phone:</strong> {record.phone_number}</p>
          <p><strong>Source:</strong> {record.from_source}</p>
          <p><strong>Store Domain:</strong> {record.store_domain}</p>
          <p><strong>Last Login:</strong> {fullDateTimeFormat(record.last_login_at)}</p>
          <p><strong>Created At:</strong> {fullDateTimeFormat(record.created_at)}</p>
          <p><strong>Status:</strong> {record.status}</p>
          <p><strong>Store Tenant ID:</strong> {record.store_tenant_id}</p>
          {record.current_subscription ? (
            <div style={{ 
              background: '#f5f5f5', 
              borderRadius: '6px', 
              padding: '12px 16px', 
              marginTop: '8px', 
              marginBottom: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ marginBottom: '6px', fontWeight: 600, color: '#333' }}>
                Current Subscription
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '14px' }}>
                <span>
                  <strong>Plan Name:</strong> {record.current_subscription.plan_name}
                </span>
                <span>
                  <strong>Status:</strong> {record.current_subscription.status}
                </span>
                <span>
                  <strong>Ngày còn lại:</strong> {record.current_subscription.remaining_days}
                </span>
                <span>
                  <strong>Start Date:</strong> {fullDateTimeFormat(record.current_subscription.start_date)}
                </span>
                <span>
                  <strong>End Date:</strong> {fullDateTimeFormat(record.current_subscription.end_date)}
                </span>
              </div>
            </div>
          ) : (
            <span style={{ color: '#888' }}>No subscription</span>
          )}
        </div>
      )
    },
    {
      title: 'Features',
      width: '30rem',
      ellipsis: {
        showTitle: false
      },
      render: (record) => {

        return (
          <div style={{ padding: '12px' }}>
            {record.current_tenant ? (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <span style={{ marginRight: '8px' }}>API Service:</span>
                  <Switch
                    checked={record.current_tenant.enable_api_service}
                    onChange={(checked) => handleFeatureToggle('api_service', checked, record)}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <span style={{ marginRight: '8px' }}>Membership:</span>
                  <Switch
                    checked={record.current_tenant?.feature_setting?.enable_membership}
                    onChange={(checked) => handleFeatureToggle('membership', checked, record)}
                  />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <span style={{ marginRight: '8px' }}>Reseller:</span>
                  <Switch
                    checked={record.current_tenant?.feature_setting?.enable_reseller}
                    onChange={(checked) => handleFeatureToggle('reseller', checked, record)}
                  />
                </div>
              </>
            ) : (
              'No features available'
            )}
          </div>
        );
      }

    },
    {
      title: 'Chức năng',
      width: '15rem',
      render: (record) => (
        <>
          <p>
            <Button type="primary" style={{ width: '170px' }} onClick={() => handleAccessAccount(record.id)}>
              Đăng nhập
            </Button>
          </p>
          <p>
            <Button type="default" style={{ width: '170px' }} onClick={() => showPasswordModal(record.id)}>
              Đổi mật khẩu
            </Button>
          </p>
          <p>
            <Button type="primary" style={{ width: '170px' }} onClick={() => showOrderPaymentModal(record.id)}>
              Create Order Payment
            </Button>
          </p>
        </>
      )
    }
  ];
  
  return (
    <>
      <AppHeader
        fixedTop
        pageTitle="Tất cả Accounts AiHub"
        pageHeaderExtras={
          <>
            <Button
              type="default"
              onClick={() => handleExport('get_trial_accounts')}
              loading={exportLoading['get_trial_accounts']}
              style={{ marginRight: 8 }}
            >
              Export Trial
            </Button>
            <Button
              type="default"
              onClick={() => handleExport('get_expiring_accounts')}
              loading={exportLoading['get_expiring_accounts']}
              style={{ marginRight: 8 }}
            >
              Export Expiring
            </Button>
            <Button
              type="default"
              onClick={() => handleExport('get_pending_payment_accounts')}
              loading={exportLoading['get_pending_payment_accounts']}
              style={{ marginRight: 8 }}
            >
              Export Pending Payment
            </Button>
            <Button type="primary" onClick={() => navigate(AppRoutes.Accounts.New.path)}>
              Thêm
            </Button>
          </>
        }
      />
      <br />
      <ContentRow capped>
      <Form
          form={searchForm}
          name="advanced_search"
          className="ant-advanced-search-form"
          onFinish={onFinishSearchForm}
          style={{marginBottom: "40px"}}
        >
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name='email'
                label='email'
              >
                <Input placeholder="Tìm theo email" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name='domain'
                label='domain'
              >
                <Input placeholder="Tìm theo domain" />
              </Form.Item>
            </Col>
            <Col
              style={{
                textAlign: 'right',
              }}
            >
              <Button type="primary" htmlType="submit">
                Search
              </Button>
              <Button
                style={{
                  margin: '0 8px',
                }}
                onClick={() => {
                  searchForm.resetFields();
                  onFinishSearchForm();
                }}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Form>
        <StyledTable
          sticky={{ offsetHeader: OFFSET_HEADER }}
          size="small"
          scroll={{ x: 1000 }}
          loading={isFetching}
          rowKey="id"
          // @ts-ignore
          columns={columns}
          dataSource={accounts}
          onChange={onChangeTable}
          pagination={{
            defaultPageSize: paginate.limit,
            defaultCurrent: paginate.page,
            total: paginate.total,
            position: [bottom],
            showTotal: (total, range) => (
              <span style={{ left: 0, position: 'absolute' }}>
                Hiển thị {range[0]}-{range[1]} của {total} tin đã đăng
              </span>
            )
          }}
        />
      </ContentRow>
      <Modal
        title="Update Password"
        open={isPasswordModalVisible}
        onOk={handleUpdatePassword}
        onCancel={() => {
          setIsPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
      >
        <Form form={passwordForm}>
          <Form.Item
            name="password"
            label="New Password"
            rules={[{ required: true, message: 'Please input new password!' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Create Order Payment"
        open={isOrderPaymentModalVisible}
        onOk={handleCreateOrderPayment}
        onCancel={() => {
          setIsOrderPaymentModalVisible(false);
          orderPaymentForm.resetFields();
        }}
        width={600}
      >
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <h4 style={{ marginBottom: '12px' }}>Current Subscription</h4>
          {selectedAccountId && accounts.find(acc => acc.id === selectedAccountId)?.current_subscription && (
            <div>
              <p><strong>Plan Name:</strong> {accounts.find(acc => acc.id === selectedAccountId).current_subscription.plan_name}</p>
              <p><strong>Status:</strong> {accounts.find(acc => acc.id === selectedAccountId).current_subscription.status}</p>
              <p><strong>Start Date:</strong> {fullDateTimeFormat(accounts.find(acc => acc.id === selectedAccountId).current_subscription.start_date)}</p>
              <p><strong>End Date:</strong> {fullDateTimeFormat(accounts.find(acc => acc.id === selectedAccountId).current_subscription.end_date)}</p>
            </div>
          )}
        </div>
        <Form form={orderPaymentForm}>
          <Form.Item
            name="plan_id"
            label="Plan ID"
            rules={[{ required: true, message: 'Please select a plan!' }]}
          >
            <Select placeholder="Select a plan">
              <Select.Option value="P11001">PRO 1 tháng</Select.Option>
              <Select.Option value="P11002">PRO 3 tháng</Select.Option>
              <Select.Option value="P11003">PRO 6 tháng</Select.Option>
              <Select.Option value="P11004">PRO 1 năm</Select.Option>
              <Select.Option value="P12001">TEAM 1 tháng</Select.Option>
              <Select.Option value="P12002">TEAM 3 tháng</Select.Option>
              <Select.Option value="P12003">TEAM 6 tháng</Select.Option>
              <Select.Option value="P12004">TEAM 1 năm</Select.Option>
              <Select.Option value="P13001">ENTERPRISE 1 tháng</Select.Option>
              <Select.Option value="P13002">ENTERPRISE 3 tháng</Select.Option>
              <Select.Option value="P13003">ENTERPRISE 6 tháng</Select.Option>
              <Select.Option value="P13004">ENTERPRISE 1 năm</Select.Option>
              <Select.Option value="P14004">BUSINESS 1 năm</Select.Option>
              <Select.Option value="P11101">PLUS 1 tháng</Select.Option>
              <Select.Option value="P11102">PLUS 3 tháng</Select.Option>
              <Select.Option value="P11103">PLUS 6 tháng</Select.Option>
              <Select.Option value="P11104">PLUS 1 năm</Select.Option>
              <Select.Option value="P12101">SCALE 1 tháng</Select.Option>
              <Select.Option value="P12102">SCALE 3 tháng</Select.Option>
              <Select.Option value="P12103">SCALE 6 tháng</Select.Option>
              <Select.Option value="P12104">SCALE 1 năm</Select.Option>
              <Select.Option value="P13101">MASTER 1 tháng</Select.Option>
              <Select.Option value="P13102">MASTER 3 tháng</Select.Option>
              <Select.Option value="P13103">MASTER 6 tháng</Select.Option>
              <Select.Option value="P13104">MASTER 1 năm</Select.Option>
              <Select.Option value="P31001">PLUS RESELLER 1 tháng</Select.Option>
              <Select.Option value="P31002">PLUS RESELLER 3 tháng</Select.Option>
              <Select.Option value="P31003">PLUS RESELLER 6 tháng</Select.Option>
              <Select.Option value="P31004">PLUS RESELLER 1 năm</Select.Option>
              <Select.Option value="P32001">SCALE RESELLER 1 tháng</Select.Option>
              <Select.Option value="P32002">SCALE RESELLER 3 tháng</Select.Option>
              <Select.Option value="P32003">SCALE RESELLER 6 tháng</Select.Option>
              <Select.Option value="P32004">SCALE RESELLER 1 năm</Select.Option>
              <Select.Option value="P33001">MASTER RESELLER 1 tháng</Select.Option>
              <Select.Option value="P33002">MASTER RESELLER 3 tháng</Select.Option>
              <Select.Option value="P33003">MASTER RESELLER 6 tháng</Select.Option>
              <Select.Option value="P33004">MASTER RESELLER 1 năm</Select.Option>
              <Select.Option value="P11201">SVIP70 1 tháng</Select.Option>
              <Select.Option value="P11202">SVIP70 3 tháng</Select.Option>
              <Select.Option value="P11203">SVIP70 6 tháng</Select.Option>
              <Select.Option value="P11204">SVIP70 1 năm</Select.Option>
              <Select.Option value="P12201">SVIP85 1 tháng</Select.Option>
              <Select.Option value="P12202">SVIP85 3 tháng</Select.Option>
              <Select.Option value="P12203">SVIP85 6 tháng</Select.Option>
              <Select.Option value="P12204">SVIP85 1 năm</Select.Option>
              <Select.Option value="P13201">SVIP95 1 tháng</Select.Option>
              <Select.Option value="P13202">SVIP95 3 tháng</Select.Option>
              <Select.Option value="P13203">SVIP95 6 tháng</Select.Option>
              <Select.Option value="P13204">SVIP95 1 năm</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="order_payment_type"
            label="Payment Type"
            rules={[{ required: true, message: 'Please select payment type!' }]}
          >
            <Select>
              <Select.Option value="normal">Đã thanh toán</Select.Option>
              <Select.Option value="free_tier">Được LVB tài trợ</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="note"
            label="Note"
            rules={[{ required: true, message: 'Please enter a note' }]}
          >
            <Input.TextArea placeholder="Enter note" rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// --- Helper functions for CSV export ---
const replacer = (key, value) => value === null || value === undefined ? '' : value;

function convertToCSV(data, headers) {
  if (!data || data.length === 0) {
    return '';
  }
  const headerRow = headers.join(',');
  const rows = data.map(row =>
    headers.map(header => JSON.stringify(row[header], replacer)).join(',')
  );
  return [headerRow, ...rows].join('\n');
}

function downloadCSV(csvString, filename) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) { // Feature detection
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up the object URL
  } else {
      message.error("CSV download is not supported in this browser.");
  }
}
// --- End Helper functions ---
